// Gems Mine — pure rules. No React, no DOM, no money. Everything here is a
// function of its inputs so the gate test can prove the ladder before a rupee
// ever rides on it, and so the phase-2 server can lift this file verbatim
// (the same seam roulette's engine.ts turned out to be).

/** The board. 5×5 is the classic Mines shape — big enough for a real climb,
 *  small enough that every tile stays a comfortable thumb target. */
export const COLS = 5;
export const ROWS = 5;
export const TILES = COLS * ROWS;

/** The three danger levels, in the owner's plain-words doctrine: no "mines
 *  count" jargon on a button — a name, a bomb count, and one line of truth. */
export type Danger = 'careful' | 'bold' | 'wild';
export const DANGERS: Record<Danger, { bombs: number; label: string; blurb: string }> = {
  careful: { bombs: 1, label: 'Careful', blurb: '1 bomb hides. Small steps, long climbs.' },
  bold:    { bombs: 3, label: 'Bold',    blurb: '3 bombs hide. Faster money, real risk.' },
  wild:    { bombs: 5, label: 'Wild',    blurb: '5 bombs hide. Huge steps. One mistake ends it.' },
};

/** The house's cut of the fair ladder. PHASE-2 SEAM: becomes the admin knob
 *  `ladder_rtp_pct` and the Target-Profit engine does the real steering — this
 *  factor is only the price printed on the rungs. */
export const LADDER_RTP = 0.97;

/**
 * TOTAL-RETURN multiplier after `picks` safe strikes with `bombs` hidden.
 *
 * Fair odds: before the k-th safe pick there are 25−k tiles left of which
 * 25−k−B are safe, so the fair rung is the product of the inverse safe
 * probabilities. × RTP, floored to 2dp so the display can never promise a
 * paisa more than settlement pays (the roulette floor-per-bet lesson).
 *
 * X-DOCTRINE: this is what the player GETS BACK — stake × rung in the wallet.
 * X2 doubles you. There is no "profit multiplier" anywhere in this game.
 */
export function rungMultiplier(bombs: number, picks: number): number {
  let m = 1;
  for (let i = 0; i < picks; i++) {
    m *= (TILES - i) / (TILES - bombs - i);
  }
  return Math.max(1, Math.floor(m * LADDER_RTP * 100) / 100);
}

/** The whole ladder for a danger level — rung 0 is "just started" (X1 —
 *  cashing out before any pick returns the stake; the UI simply disables it). */
export function ladder(bombs: number): number[] {
  const out: number[] = [];
  for (let k = 0; k <= TILES - bombs; k++) out.push(rungMultiplier(bombs, k));
  return out;
}

/** Gross return for a cashout, floored to whole rupees like every settle. */
export function cashoutReturn(stake: number, bombs: number, picks: number): number {
  return Math.floor(stake * rungMultiplier(bombs, picks));
}

/**
 * PRACTICE-MODE bomb placement — honest uniform. PHASE-2 SEAM: deleted
 * outright; the server decides gem-or-bomb PER STRIKE through the quarter
 * engine (the thimbles pattern — no committed layout exists to leak).
 */
export function placeBombs(bombs: number, rnd: () => number): Set<number> {
  const s = new Set<number>();
  while (s.size < bombs) s.add(Math.floor(rnd() * TILES));
  return s;
}

/** The five gem kinds. Purely cosmetic — every gem pays the same rung; the
 *  variety is what keeps 20 reveals from looking like one reveal repeated. */
export type GemKind = 'emerald' | 'ruby' | 'sapphire' | 'amber' | 'diamond';
export const GEM_KINDS: GemKind[] = ['emerald', 'ruby', 'sapphire', 'amber', 'diamond'];

/** Which gem a tile hides — stable per (round, tile) so a re-render can never
 *  recolour a revealed gem. Diamond is the rare one (it reads as special). */
export function gemFor(roundSeed: number, tile: number): GemKind {
  /* full murmur3 finaliser — one xorshift round left sequential seeds
     correlated (diamonds came up 12% instead of 6% in the gate) */
  let h = (roundSeed ^ Math.imul(tile + 1, 0x9e3779b9)) >>> 0;
  h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b) >>> 0;
  h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35) >>> 0;
  h = (h ^ (h >>> 16)) >>> 0; // XOR yields a SIGNED int32 — force unsigned
  const r = h / 4294967296;
  if (r < 0.06) return 'diamond';
  const rest: GemKind[] = ['emerald', 'ruby', 'sapphire', 'amber'];
  return rest[Math.floor((r - 0.06) / 0.94 * 4) % 4];
}

/** Format a rung the way every surface shows it: X1.13, X2, X24.5. Trailing
 *  zeros are noise on a button. */
export function fmtX(m: number): string {
  const s = m.toFixed(2).replace(/\.?0+$/, '');
  return `X${s}`;
}
